// §manufacturer-pricing — what the MANUFACTURER charges the patient directly,
// shown alongside (never added to) what Fitpal charges.
//
// ─────────────────────────────────────────────────────────────────────────────
// ⚠️  THIS NUMBER IS NEVER CHARGED BY US AND MUST NEVER REACH THE PAYMENT PATH.
//
//     On the manufacturer route the patient pays Fitpal the CONSULT FEE only
//     ($59). LillyDirect / NovoCare bill them separately for the medication.
//     If a value from this file ever reaches `totalCents` or SquarePaymentForm's
//     `amount`, we charge a patient several hundred dollars for a drug we are
//     not dispensing. Nothing here is imported by any total computation, and
//     manufacturerPricing.test.js asserts the cart total is unchanged while a
//     price from this file is on screen. Keep it that way.
// ─────────────────────────────────────────────────────────────────────────────
//
// Provenance: supplied by the owner 2026-09-08 from the manufacturers' published
// self-pay pricing. We did NOT scrape it and we cannot see when the
// manufacturers last changed it — NovoCare's page carries no "as of" stamp, only
// a right-to-modify disclaimer. So the attribution dates OUR CAPTURE and never
// the price itself; claiming otherwise would assert a freshness the source never
// offered, on a screen where money is involved.

// When the numbers below were given to us. Not when the manufacturer set them.
export const PRICING_CAPTURED_ON = '2026-09-08';

// After this many days we stop showing a figure and say where to check instead.
// Manufacturer self-pay pricing moves and a stale number on a checkout screen is
// worse than no number: the patient budgets against it and is surprised later.
export const PRICING_STALE_AFTER_DAYS = 90;

// Cents, keyed by the EXACT catalog product name (the same join used by
// brandedProducts.skuMatch, and for the same reason: the catalog carries no
// manufacturer, form or dose field we could key on instead).
//
// A missing entry is not an error — it renders no price block at all. That is
// deliberate: an absent price is a gap we disclose by staying silent, whereas a
// guessed price is a number a patient plans around.
export const MANUFACTURER_PRICE_CENTS = {
  // ── NovoCare (Novo Nordisk) ────────────────────────────────────────────────
  'Wegovy Tablets Semaglutide 1.5mg': 14000,
  'Wegovy Tablets Semaglutide 4mg': 19900,
  'Wegovy Tablets Semaglutide 9mg': 29900,
  'Wegovy Tablets Semaglutide 25mg': 29900,

  'Wegovy 0.25mg  (Semaglutide)': 19900, // NB: two spaces, as the catalog has it
  'Wegovy 0.5mg (Semaglutide)': 19900,
  'Wegovy 1mg (Semaglutide)': 34900,
  'Wegovy 1.7mg (Semaglutide)': 34900,
  'Wegovy 2.4mg (Semaglutide)': 34900,
  // Wegovy 7.2mg pen was priced at $399 but has no catalog SKU, so there is
  // nothing to key it to. Left out rather than added speculatively.

  // Ozempic pens are DELIBERATELY ABSENT — no prices were supplied for them.
  // All four SKUs are live, so an Ozempic patient sees no manufacturer price
  // until NovoCare's figures are provided. See §GAPS in the PR / report.

  // ── LillyDirect (Eli Lilly) ───────────────────────────────────────────────
  'Foundayo Tablets Orforglipron 0.8mg': 14900,
  // 'Foundayo Tablets Orforglipron 2.5mg' — OMITTED ON PURPOSE. The figure
  // supplied read "$4149", which cannot be right beside $149 / $199 / $299
  // neighbours. Rather than pick the likely $149, this dose shows no price until
  // it is confirmed. A wrong price here is one a patient budgets against.
  'Foundayo Tablets Orforglipron 5.5mg': 19900,
  'Foundayo Tablets Orforglipron 9mg': 29900,
  'Foundayo Tablets Orforglipron 14.5mg': 29900,
  'Foundayo Tablets Orforglipron 17.2mg': 29900,

  'Zepbound 2.5mg (Tirzepatide)': 29900,
  'Zepbound 5mg (Tirzepatide)': 39900,
  'Zepbound 7.5mg (Tirzepatide)': 44900,
  'Zepbound 10mg (Tirzepatide)': 44900,
  'Zepbound 12.5mg (Tirzepatide)': 44900,
  'Zepbound 15mg  (Tirzepatide)': 44900, // two spaces, as the catalog has it

  // Mounjaro: every dose supplied at $499. The first three shared one arrow
  // ("2.5mg/0.5ml, 5mg/0.5ml, 7.5mg/.5 -> $499"), read as applying to all three
  // since every other Mounjaro dose is also $499. Flagged for confirmation.
  'Mounjaro 2.5mg (Tirzepatide)': 49900,
  'Mounjaro 5mg (Tirzepatide)': 49900,
  'Mounjaro 7.5mg (Tirzepatide)': 49900,
  'Mounjaro 10mg (Tirzepatide)': 49900,
  'Mounjaro 12.5mg (Tirzepatide)': 49900,
  'Mounjaro 15mg (Tirzepatide)': 49900,
};

const norm = (s) => String(s || '').trim();

const daysSince = (iso, now) => {
  const then = Date.parse(`${iso}T00:00:00Z`);
  if (!Number.isFinite(then)) return Infinity;
  return Math.floor((now - then) / 86400000);
};

/**
 * What the manufacturer charges for this SKU, and how to talk about it.
 *
 * @param {object} item     the catalog row the patient has selected
 * @param {object} mfr      the MANUFACTURER_PHARMACIES entry for it
 * @param {number} nowMs    injected for tests; defaults to now
 * @returns {null | {cents:number|null, brand:string, capturedOn:string, stale:boolean}}
 *   null when we have no figure for this SKU — render nothing at all.
 *   `cents: null` with `stale: true` when the capture has aged out: we still say
 *   who sets the price, but we stop quoting a number.
 */
export function resolveManufacturerPrice(item, mfr, nowMs = Date.now()) {
  if (!item || !mfr) return null;
  const name = norm(item.name || item.medication);
  const cents = MANUFACTURER_PRICE_CENTS[name];
  if (!Number.isFinite(cents)) return null;
  const stale = daysSince(PRICING_CAPTURED_ON, nowMs) > PRICING_STALE_AFTER_DAYS;
  return {
    cents: stale ? null : cents,
    brand: mfr.brand,
    capturedOn: PRICING_CAPTURED_ON,
    stale,
  };
}

const usd = (cents) => `$${((Number(cents) || 0) / 100).toFixed(2)}`;

/**
 * The line the patient reads. Two jobs, and the second matters more:
 * say what the manufacturer charges, and say that WE are not charging it.
 */
export function manufacturerPriceCopy(price) {
  if (!price) return null;
  if (price.stale || price.cents == null) {
    return {
      amount: null,
      label: `Billed by ${price.brand}`,
      detail: `${price.brand} sets this price and may change it. Check ${price.brand} for the current amount — it is not charged here.`,
    };
  }
  return {
    amount: usd(price.cents),
    label: `${price.brand} price for this dose`,
    detail: `Billed by ${price.brand}, not charged here. Captured ${price.capturedOn}; ${price.brand} may change it at any time.`,
  };
}
